import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';

// Module-level cache for PDF assets - allows instant access on subsequent opens without Redux state bloat
const pdfCache = new Map();

export const usePdfAssets = (isSplitViewOpen, ocrInputID) => {
  const [fetchedPdfs, setFetchedPdfs] = useState([]);
  const [isLoadingPdfs, setIsLoadingPdfs] = useState(false);
  const prevOcrInputIDRef = useRef(ocrInputID);

  // Fetch PDF assets from API when split view opens
  useEffect(() => {
    const fetchPdfAssets = async () => {
      if (!isSplitViewOpen) {
        return;
      }

      // Check if ocrInputID is valid (not null, undefined, or empty string)
      if (!ocrInputID || ocrInputID === '' || ocrInputID === 'null' || ocrInputID === 'undefined') {
        return;
      }

      // Check cache first for instant access on subsequent opens
      if (pdfCache.has(ocrInputID)) {
        const cachedPdfs = pdfCache.get(ocrInputID);
        setFetchedPdfs(cachedPdfs);
        setIsLoadingPdfs(false);
        return;
      }

      setIsLoadingPdfs(true);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const assetsUrl = `${API_BASE_URL}/api/items/ocr_json_input/${ocrInputID}/assets/`;
        
        // Fetch list of PDF assets
        const response = await apiFetch(assetsUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF assets: ${response.status}`);
        }

        const assetsList = await response.json();
        
        if (!Array.isArray(assetsList) || assetsList.length === 0) {
          setFetchedPdfs([]);
          setIsLoadingPdfs(false);
          return;
        }

        // Fetch each PDF and convert to File
        const pdfPromises = assetsList.map(async (asset) => {
          try {
            const pdfUrl = `${API_BASE_URL}/api/items/ocr_json_input/assets/${asset.id}`;
            const pdfResponse = await apiFetch(pdfUrl, {
              method: 'GET',
            });

            if (!pdfResponse.ok) {
              throw new Error(`Failed to fetch PDF ${asset.id}: ${pdfResponse.status}`);
            }

            // Check content type to determine how to handle the response
            const contentType = pdfResponse.headers.get('content-type') || '';
            let blob;
            
            if (contentType.includes('application/pdf') || contentType.includes('application/octet-stream')) {
              // Response is binary PDF data
              blob = await pdfResponse.blob();
            } else {
              // Try to handle as base64 or text
              const responseText = await pdfResponse.text();
              
              // Check if it's a data URL (data:application/pdf;base64,...)
              if (responseText.startsWith('data:')) {
                const base64Match = responseText.match(/base64,(.+)/);
                if (base64Match) {
                  const base64Data = base64Match[1];
                  const binaryString = atob(base64Data);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  blob = new Blob([bytes], { type: 'application/pdf' });
                } else {
                  throw new Error(`Invalid data URL format for PDF ${asset.id}`);
                }
              } else {
                // Try to decode as base64 (might have whitespace/newlines)
                try {
                  const cleanBase64 = responseText.trim().replace(/\s/g, '');
                  const binaryString = atob(cleanBase64);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  blob = new Blob([bytes], { type: 'application/pdf' });
                } catch (base64Error) {
                  // If base64 decoding fails, try treating as binary
                  const bytes = new Uint8Array(responseText.length);
                  for (let i = 0; i < responseText.length; i++) {
                    bytes[i] = responseText.charCodeAt(i);
                  }
                  blob = new Blob([bytes], { type: 'application/pdf' });
                }
              }
            }
            
            const file = new File([blob], `document_${asset.id}.pdf`, {
              type: 'application/pdf',
              lastModified: Date.now(),
            });

            return {
              id: asset.id,
              file: file,
              url: asset.url,
            };
          } catch (error) {
            console.error(`Error fetching PDF ${asset.id}:`, error);
            return null;
          }
        });

        const pdfs = (await Promise.all(pdfPromises)).filter(pdf => pdf !== null);
        
        // Cache the fetched PDFs for instant access on subsequent opens
        pdfCache.set(ocrInputID, pdfs);
        
        setFetchedPdfs(pdfs);
      } catch (error) {
        console.error('Error fetching PDF assets:', error);
        setFetchedPdfs([]);
      } finally {
        setIsLoadingPdfs(false);
      }
    };

    fetchPdfAssets();
  }, [isSplitViewOpen, ocrInputID]);

  // Clear fetched PDFs from state when split view closes (but keep in cache for instant access on next open)
  useEffect(() => {
    if (!isSplitViewOpen) {
      setFetchedPdfs(prevPdfs => {
        prevPdfs.forEach(pdf => {
          if (pdf.file && pdf.file instanceof File) {
            // Revoke any object URLs if they exist
            if (pdf.url && pdf.url.startsWith('blob:')) {
              URL.revokeObjectURL(pdf.url);
            }
          }
        });
        return [];
      });
      setIsLoadingPdfs(false);
    }
  }, [isSplitViewOpen]);

  // Clear cache for previous ocrInputID when it changes (workflow switch) to prevent stale data
  useEffect(() => {
    const prevOcrInputID = prevOcrInputIDRef.current;
    if (prevOcrInputID && prevOcrInputID !== ocrInputID) {
      pdfCache.delete(prevOcrInputID);
    }
    prevOcrInputIDRef.current = ocrInputID;
  }, [ocrInputID]);

  return { fetchedPdfs, isLoadingPdfs };
};

